import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { d as designTokens } from '../../unsorted/index2.js';
import { AnimatePresence, motion } from 'framer-motion';
import { useMemo, Children } from 'react';
import { m as merge } from '../../unsorted/index.js';
import { splitApartChildrenAndSubComponents } from '../../hooks/useSplitApartChildrenAndSubComponents.js';
import { b as ToastMessage, a as ToastButtCon, T as ToastButton } from '../../components/ToastMessage/ToastMessage.js';
import { u as useOverlaysStore } from '../../providers/overlaysContext/overlaysContext.js';
import { vFlex, hFlex } from '../../utils/sxChunks.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import '@emotion/react';
import { B as Box } from '../../components/Box/Box.js';
import { isChildSubcomponent } from '../../utils/subcomponentHelpers.js';

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// @NOTE: margin is applied to each one, so that when toasts are
// butting up against the edge of the screen, there's a small
// gap - for visual aesthetics
function getToastGroupPositionStyles(group) {
  switch (group) {
    case "topLeft":
      return {
        top: "base.spacing.x6",
        left: "base.spacing.x6",
        marginRight: "base.spacing.x4"
      };
    case "topCenter":
      return {
        top: "base.spacing.x6",
        left: "50%",
        translate: "-50% 0",
        marginLeft: "base.spacing.x4",
        marginRight: "base.spacing.x4"
      };
    case "bottomLeft":
      return {
        bottom: "base.spacing.x6",
        left: "base.spacing.x6",
        marginRight: "base.spacing.x4"
      };
    case "bottomRight":
      return {
        bottom: "base.spacing.x6",
        right: "base.spacing.x6",
        marginLeft: "base.spacing.x4"
      };
    case "bottomCenter":
      return {
        bottom: "base.spacing.x6",
        left: "50%",
        translate: "-50% 0",
        marginLeft: "base.spacing.x4",
        marginRight: "base.spacing.x4"
      };
    case "topRight":
    default:
      {
        return {
          top: "base.spacing.x6",
          right: "base.spacing.x6",
          marginLeft: "base.spacing.x6"
        };
      }
  }
}
var RIGHT_TRANSLATE = "12px";
var LEFT_TRANSLATE = "-12px";
var TOP_TRANSLATE = "-12px";
var BOTTOM_TRANSLATE = "12px";
var NO_TRANSLATE = "0px";
function ToastOverlay() {
  var _useOverlaysStore = useOverlaysStore(function (state) {
      return state.toastList;
    }),
    toastList = _useOverlaysStore.state;
  var toastsGroupedByPosition = useMemo(function () {
    return {
      topLeft: toastList.filter(function (toast) {
        var _toast$position = toast.position,
          x = _toast$position.x,
          y = _toast$position.y;
        return y === "top" && x === "left";
      }),
      topCenter: toastList.filter(function (toast) {
        var _toast$position2 = toast.position,
          x = _toast$position2.x,
          y = _toast$position2.y;
        return y === "top" && x === "center";
      }),
      topRight: toastList.filter(function (toast) {
        var _toast$position3 = toast.position,
          x = _toast$position3.x,
          y = _toast$position3.y;
        return y === "top" && x === "right";
      }),
      bottomLeft: toastList.filter(function (toast) {
        var _toast$position4 = toast.position,
          x = _toast$position4.x,
          y = _toast$position4.y;
        return y === "bottom" && x === "left";
      }),
      bottomRight: toastList.filter(function (toast) {
        var _toast$position5 = toast.position,
          x = _toast$position5.x,
          y = _toast$position5.y;
        return y === "bottom" && x === "right";
      }),
      bottomCenter: toastList.filter(function (toast) {
        var _toast$position6 = toast.position,
          x = _toast$position6.x,
          y = _toast$position6.y;
        return y === "bottom" && x === "center";
      })
    };
  }, [toastList]);
  return jsx(AnimatePresence, {
    children: Object.keys(toastsGroupedByPosition).map(function (key) {
      var typedKey = key;
      var group = toastsGroupedByPosition[typedKey];
      if (group.length === 0) return null;
      return jsx(Box, {
        "data-toast-group": typedKey,
        testId: "toastsContainer--".concat(typedKey),
        sx: _objectSpread(_objectSpread(_objectSpread({}, vFlex), getToastGroupPositionStyles(typedKey)), {}, {
          gap: "base.spacing.x2",
          position: "fixed",
          zIndex: "base.zLevel.toast",
          alignItems: "center"
        }),
        rc: jsx(motion.div, {
          initial: _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
            opacity: 0
          }, typedKey === "topRight" && {
            x: RIGHT_TRANSLATE
          }), typedKey === "topLeft" && {
            x: LEFT_TRANSLATE
          }), typedKey === "topCenter" && {
            y: TOP_TRANSLATE
          }), typedKey === "bottomRight" && {
            x: RIGHT_TRANSLATE
          }), typedKey === "bottomLeft" && {
            x: LEFT_TRANSLATE
          }), typedKey === "bottomCenter" && {
            y: BOTTOM_TRANSLATE
          }),
          exit: _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
            opacity: 0
          }, typedKey === "topRight" && {
            x: RIGHT_TRANSLATE
          }), typedKey === "topLeft" && {
            x: LEFT_TRANSLATE
          }), typedKey === "topCenter" && {
            y: TOP_TRANSLATE
          }), typedKey === "bottomRight" && {
            x: RIGHT_TRANSLATE
          }), typedKey === "bottomLeft" && {
            x: LEFT_TRANSLATE
          }), typedKey === "bottomCenter" && {
            y: BOTTOM_TRANSLATE
          }),
          animate: _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
            opacity: 1
          }, typedKey === "topRight" && {
            x: NO_TRANSLATE
          }), typedKey === "topLeft" && {
            x: NO_TRANSLATE
          }), typedKey === "topCenter" && {
            y: NO_TRANSLATE
          }), typedKey === "bottomRight" && {
            x: NO_TRANSLATE
          }), typedKey === "bottomLeft" && {
            x: NO_TRANSLATE
          }), typedKey === "bottomCenter" && {
            y: NO_TRANSLATE
          }),
          transition: {
            duration: designTokens.base.motion.normal.fast.jsDuration,
            ease: designTokens.base.motion.normal.fast.jsEase
          }
        }),
        children: jsx(AnimatePresence, {
          children: group.map(function (toast) {
            var id = toast.id,
              content = toast.content,
              hideDismissButton = toast.hideDismissButton,
              variant = toast.variant,
              onCloseToast = toast.onCloseToast,
              buttonsPosition = toast.buttonsPosition,
              testId = toast.testId,
              _toast$sx = toast.sx,
              sx = _toast$sx === void 0 ? {} : _toast$sx;
            var _splitApartChildrenAn = splitApartChildrenAndSubComponents(content, [ToastMessage, ToastButtCon, ToastButton]),
              otherChildren = _splitApartChildrenAn.otherChildren;
            return jsxs(Box, {
              "data-id": id,
              testId: testId !== null && testId !== void 0 ? testId : "toastsContainer__toast",
              sx: merge(_objectSpread(_objectSpread({}, buttonsPosition === "right" ? _objectSpread(_objectSpread({}, hFlex), {}, {
                alignItems: "center",
                justifyContent: "space-between"
              }) : _objectSpread({}, vFlex)), {}, {
                gap: "base.spacing.x4",
                bg: "base.color.translucent.standard.1000",
                p: "base.spacing.x2",
                pr: "base.spacing.x3",
                pl: "base.spacing.x9",
                brad: "base.borderRadius.x6",
                minw: "230px",
                maxw: "450px",
                minh: "64px",
                boxShadow: "base.shadow.500",
                position: "relative"
              }), sx),
              rc: jsx(motion.div, {
                initial: _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
                  opacity: 0
                }, typedKey === "topRight" && {
                  x: RIGHT_TRANSLATE
                }), typedKey === "topLeft" && {
                  x: LEFT_TRANSLATE
                }), typedKey === "topCenter" && {
                  y: TOP_TRANSLATE
                }), typedKey === "bottomRight" && {
                  x: RIGHT_TRANSLATE
                }), typedKey === "bottomLeft" && {
                  x: LEFT_TRANSLATE
                }), typedKey === "bottomCenter" && {
                  y: BOTTOM_TRANSLATE
                }),
                exit: _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
                  opacity: 0
                }, typedKey === "topRight" && {
                  x: RIGHT_TRANSLATE
                }), typedKey === "topLeft" && {
                  x: RIGHT_TRANSLATE
                }), typedKey === "topCenter" && {
                  y: TOP_TRANSLATE
                }), typedKey === "bottomRight" && {
                  x: RIGHT_TRANSLATE
                }), typedKey === "bottomLeft" && {
                  x: LEFT_TRANSLATE
                }), typedKey === "bottomCenter" && {
                  y: BOTTOM_TRANSLATE
                }),
                animate: _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
                  opacity: 1
                }, typedKey === "topRight" && {
                  x: NO_TRANSLATE
                }), typedKey === "topLeft" && {
                  x: NO_TRANSLATE
                }), typedKey === "topCenter" && {
                  y: NO_TRANSLATE
                }), typedKey === "bottomRight" && {
                  x: NO_TRANSLATE
                }), typedKey === "bottomLeft" && {
                  x: NO_TRANSLATE
                }), typedKey === "bottomCenter" && {
                  y: NO_TRANSLATE
                }),
                transition: {
                  duration: designTokens.base.motion.normal.fast.jsDuration,
                  ease: designTokens.base.motion.normal.fast.jsEase
                }
              }),
              children: [jsx(Box, {
                testId: "toastVariantBar",
                sx: {
                  top: "base.spacing.x2",
                  bottom: "base.spacing.x2",
                  position: "absolute",
                  left: "base.spacing.x2",
                  width: "6px",
                  brad: "base.borderRadius.x6",
                  bg: "base.color.status.".concat(variant, ".bright")
                }
              }), Children.map(content, function (child) {
                return isChildSubcomponent(child, ToastMessage) ? child : null;
              }), jsxs(Box, {
                sx: _objectSpread(_objectSpread(_objectSpread({}, hFlex), buttonsPosition === "bottomRight" ? {
                  justifyContent: "flex-end"
                } : {}), {}, {
                  gap: "base.spacing.x1"
                }),
                children: [Children.map(content, function (child) {
                  return isChildSubcomponent(child, ToastButtCon) || isChildSubcomponent(child, ToastButton) ? child : null;
                }), !hideDismissButton ? jsx(ToastButton, {
                  testId: "dismissToastButton",
                  onClick: function onClick() {
                    onCloseToast === null || onCloseToast === void 0 ? void 0 : onCloseToast();
                  },
                  children: "Dismiss"
                }) : null]
              }), otherChildren]
            }, id);
          })
        })
      }, typedKey);
    })
  });
}

export { ToastOverlay as T };
//# sourceMappingURL=ToastOverlay.js.map
